import React from 'react';
import {
  Box, Button, Chip, CircularProgress, IconButton, MenuItem, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon, Edit as EditIcon, FilterListOff as ClearFiltersIcon, History as HistoryIcon,
  Inbox as InboxIcon, VpnKey as KeyIcon,
} from '@mui/icons-material';
import { entryDisplayColumns, formatEntryValue } from '../utils/registryAttributes';
import { MONO_FONT } from '../constants/typography';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const FilterCell = ({ col, value, onChange }) => {
  const label = `Filtra ${col.label}`;
  const options = col.type === 'boolean'
    ? [{ value: 'true', label: 'Sì' }, { value: 'false', label: 'No' }]
    : Array.isArray(col.field?.enum) ? col.field.enum.map((v) => ({ value: String(v), label: String(v) })) : null;
  if (options) {
    return (
      <TextField
        select size="small" fullWidth value={value ?? ''} onChange={(e) => onChange(col.key, e.target.value)}
        SelectProps={{ displayEmpty: true }} inputProps={{ 'aria-label': label }}
      >
        <MenuItem value=""><em>Tutti</em></MenuItem>
        {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
      </TextField>
    );
  }
  return (
    <TextField
      size="small" fullWidth placeholder="Cerca…" value={value ?? ''}
      onChange={(e) => onChange(col.key, e.target.value)} inputProps={{ 'aria-label': label }}
    />
  );
};

const MessageRow = ({ colSpan, children }) => (
  <TableRow>
    <TableCell colSpan={colSpan} sx={{ py: 6, textAlign: 'center', color: 'text.secondary', borderBottom: 0 }}>
      {children}
    </TableCell>
  </TableRow>
);

/** Tabella presentazionale delle voci: sort per colonna, filtro per campo, paginazione server-side. */
const RegistryEntriesTable = ({
  schema, entries, totalElements, loading, error,
  filters, onFilterChange, hasActiveFilters, onClearFilters,
  sort, onSortChange, page, rowsPerPage, onPageChange, onRowsPerPageChange,
  canEdit, onEdit, onDelete, onHistory,
}) => {
  const columns = entryDisplayColumns(schema);
  // La colonna Azioni c'è per chi modifica o, con lo storico, anche per chi legge soltanto.
  const hasActions = Boolean(canEdit || onHistory);
  const colSpan = columns.length + (hasActions ? 1 : 0);

  let body;
  if (loading) {
    body = <MessageRow colSpan={colSpan}><CircularProgress size={28} /></MessageRow>;
  } else if (error) {
    body = <MessageRow colSpan={colSpan}><Typography color="error">{error}</Typography></MessageRow>;
  } else if (entries.length === 0) {
    body = (
      <MessageRow colSpan={colSpan}>
        <InboxIcon sx={{ fontSize: 44, opacity: 0.35, mb: 0.5 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {hasActiveFilters ? 'Nessuna voce corrisponde ai filtri' : 'Nessuna voce'}
        </Typography>
        {!hasActiveFilters && canEdit && (
          <Typography variant="body2">Aggiungi la prima voce con «Nuova voce».</Typography>
        )}
      </MessageRow>
    );
  } else {
    body = entries.map((entry) => (
      <TableRow key={entry.id} hover>
        {columns.map((col) => {
          const raw = entry.attributes?.[col.key];
          const isEmpty = raw === null || raw === undefined || raw === '';
          return (
            <TableCell key={col.key}>
              {col.isKey && !isEmpty ? (
                <Chip size="small" variant="outlined" label={String(raw)} sx={{ fontFamily: MONO_FONT, fontWeight: 500 }} />
              ) : isEmpty ? (
                <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
              ) : (
                formatEntryValue(col.field, raw)
              )}
            </TableCell>
          );
        })}
        {hasActions && (
          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
            {onHistory && (
              <Tooltip title="Storico">
                <IconButton size="small" aria-label="Storico" onClick={() => onHistory(entry)}><HistoryIcon fontSize="small" /></IconButton>
              </Tooltip>
            )}
            {canEdit && (
              <>
                <Tooltip title="Modifica">
                  <IconButton size="small" aria-label="Modifica" onClick={() => onEdit(entry)}><EditIcon fontSize="small" /></IconButton>
                </Tooltip>
                <Tooltip title="Elimina">
                  <IconButton size="small" aria-label="Elimina" onClick={() => onDelete(entry)}><DeleteIcon fontSize="small" /></IconButton>
                </Tooltip>
              </>
            )}
          </TableCell>
        )}
      </TableRow>
    ));
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {`${totalElements} ${totalElements === 1 ? 'voce' : 'voci'}`}
        </Typography>
        <Button size="small" startIcon={<ClearFiltersIcon />} onClick={onClearFilters} disabled={!hasActiveFilters}>
          Azzera filtri
        </Button>
      </Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
              {columns.map((col) => {
                const active = sort?.field === col.key;
                return (
                  <TableCell key={col.key} sortDirection={active ? sort.direction : false}>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <TableSortLabel active={active} direction={active ? sort.direction : 'asc'} onClick={() => onSortChange(col.key)}>
                        {col.label}
                      </TableSortLabel>
                      {col.isKey && (
                        <Tooltip title="Chiave univoca della voce">
                          <KeyIcon sx={{ fontSize: 15, color: 'primary.main' }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                );
              })}
              {hasActions && <TableCell align="right">Azioni</TableCell>}
            </TableRow>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.key} sx={{ py: 0.75 }}>
                  <FilterCell col={col} value={filters?.[col.key]} onChange={onFilterChange} />
                </TableCell>
              ))}
              {hasActions && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>{body}</TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div" count={totalElements} page={page} rowsPerPage={rowsPerPage}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        onPageChange={(_e, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
        labelRowsPerPage="Righe per pagina:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
        getItemAriaLabel={(type) => (type === 'next' ? 'Pagina successiva' : type === 'previous' ? 'Pagina precedente' : type)}
      />
    </Box>
  );
};

export default RegistryEntriesTable;
